import Factory from '@leverege/factory';
import { getAccess } from '../Util.js';
const sortDefault = (valA, valB) => {
  let a = valA;
  let b = valB;
  // force null and undefined to the bottom
  a = a === null || a === undefined ? '' : a;
  b = b === null || b === undefined ? '' : b;
  // force any string values to lowercase
  a = typeof a === 'string' ? a.toLowerCase() : a;
  b = typeof b === 'string' ? b.toLowerCase() : b;
  // Return either 1 or -1 to indicate a sort priority
  if (a > b) {
    return 1;
  }
  if (a < b) {
    return -1;
  }

  // returning 0, undefined or any falsey value will use subsequent sorts or
  // the index as a tiebreaker
  return 0;
};
const SortFactory = new Factory({
  name: 'SortFactory',
  registry: {
    default: sortDefault
  },
  defaultObject: sortDefault,
  strategy: Factory.LOOKUP
});
class AbstractSortController {
  /**
   * Generic sorting class
   */
  constructor() {
    if (this.constructor === AbstractSortController) {
      throw new TypeError('Abstract class "AbstractSortController" can not be instantiated');
    }
    if (typeof this.key !== 'function') {
      throw new TypeError('Classes that subclass AbstractSortController must implement method "key"');
    }
    if (typeof this.set !== 'function') {
      throw new TypeError('Classes that subclass AbstractSortController must implement method "set"');
    }
    if (typeof this.isColumnSorted !== 'function') {
      throw new TypeError('Classes that subclass AbstractSortController must implement method "isColumnSorted"');
    }
    if (typeof this.sort !== 'function') {
      throw new TypeError('Classes that subclass AbstractSortController must implement method "sort"');
    }
    if (typeof this.getSortDirection !== 'function') {
      throw new TypeError('Classes that subclass AbstractSortController must implement method "getSortDirection"');
    }
  }
}
class SortController extends AbstractSortController {
  /**
   * Sorting class which sorts one column at a time and stores its sort state on the state
   * of a provided React component.
   * 
   * @param {React.Component} component - Parent component where sorting state changes will be managed
   * @param {string} stateKey - The key within the parent component's state where sorting state will be stored.
   * @param {Object} options - idKey - The key within the column object to find the column's id
   *                           idFunc - A function that returns a column's id
   */
  // eslint-disable-next-line default-param-last
  constructor(stateKey, lookup = {}, component, options) {
    super(stateKey, lookup, component, options);
    const {
      idKey,
      idFunc
    } = options;
    this.stateKey = stateKey;
    this.lookup = lookup;
    this.component = component;
    this.idKey = idKey;
    this.idFunc = idFunc;
    this.lastData = null;
    this.lastSortedData = null;
    this.lastSortModel = null;
  }

  /**
   * * Returns a unique id key for the given column
   * 
   * @param {Object} column - The column object for which to return a key
   * 
   * @return {string} - The id key for the column
   */
  key(column) {
    // If we've been given a sort key, just return it
    if (typeof column === 'string') {
      return column;
    }
    if (this.idFunc) {
      return this.idFunc(column);
    }
    return this.idKey ? column[this.idKey] : column && column.id;
  }

  /**
   * 
   * @param {Object} column - The column object
   * 
   * @return {boolean} - Whether or not the column is sorted
   */
  isColumnSorted(column, sorts) {
    const key = this.key(column);
    const item = sorts[key];
    return !!item;
  }
  needsUpdate(data, sortModel) {
    return this.lastData !== data || this.lastSortModel !== sortModel;
  }

  /**
   * Sorts the tabular data based on established sort model using 
   * 
   * @param {Array} data - The table data
   * @param {Array} columns - The table columns
   * @param {function=} sortOverride - An override function to be used for sorting. Method signature is itemA, itemB, accessor
   */
  sort(data, sortModel) {
    if (!this.needsUpdate(data)) {
      return this.lastSortedData;
    }

    // Currently handles only single sorts
    const key = sortModel && Object.keys(sortModel).length > 0 && Object.keys(sortModel)[0];
    if (key && this.lookup[key]) {
      const model = sortModel[key];
      const direction = model.direction;
      const accessor = getAccess(this.lookup[key]);
      const sortFunction = SortFactory.create(model);
      const nData = [...data].sort((a, b) => {
        const valA = accessor(a);
        const valB = accessor(b);
        if (direction === 'asc') {
          return sortFunction(valA, valB);
        }
        return sortFunction(valB, valA);
      });
      this.lastData = data;
      this.lastSortedData = nData;
      this.lastSortModel = sortModel;
      return this.lastSortedData;
    }
    this.lastData = data;
    this.lastSortedData = data;
    this.lastSortModel = sortModel;
    return data;
  }

  /**
   * Returns the direction that a given column is sorted based on sort state
   * 
   * @param {Object} column - The column for which to get sort direction
   * 
   * @return {string} - one of 'asc', 'desc' or null
   */
  getSortDirection(column, sorts) {
    const key = this.key(column);
    if (sorts[key]) {
      return sorts[key].direction;
    }
    return null;
  }

  /**
   * Sets a column's sort direction in state, starting with ascending.
   * 
   * @param {Object} column - The column to sort by
   * @param {string=} direciton - asc or desc. Optional and the default is ascending OR the opposite of the current direction
   */

  set(sorts) {
    this.component.setState({
      [this.stateKey]: {
        ...sorts
      }
    });
  }
  toggle(key) {
    const sorts = this.component.state[this.stateKey];
    const sort = sorts[key] || {};
    const direction = sort.direction === 'asc' ? 'desc' : 'asc';
    this.component.setState({
      [this.stateKey]: {
        ...sorts,
        [key]: {
          type: 'default',
          ...sort,
          direction
        }
      }
    });
  }
  remove(keys) {
    const sorts = this.component.state[this.stateKey];
    const nSorts = {
      ...sorts
    };
    keys.forEach(key => {
      delete nSorts[key];
    });
    this.component.setState({
      [this.stateKey]: {
        ...nSorts
      }
    });
  }
  clear() {
    this.component.setState({
      [this.stateKey]: {}
    });
  }
}
export { SortController, AbstractSortController, SortFactory, sortDefault };